<script lang="ts" setup>
import { computed, inject, ref, unref } from 'vue'
import type { ScrollChapter } from '../composables/useInfiniteChapter'
import type { ChapterConfig } from '@/composables/payment'
import { CHARGE_TYPE } from '@/constants'
import { useI18n } from '@/composables/useI18n'
import { brand } from '@/config/brands'

interface Props {
  chapter: ScrollChapter
  memberCoin: number
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'unlock'): void
}>()

const config = inject<ChapterConfig>('chapterConfig')!
const theme = inject<any>('theme', { background: '#F6F7F9' })
const autoLock = ref(true)
const { t } = useI18n()
const themeBackground = computed(() => unref(theme)?.background?.toUpperCase?.() || '#F6F7F9')
const isLightTheme = computed(() => themeBackground.value !== '#292929')
const unlockDividerLockIcon = computed(() => `url(/images/${brand}/chapter/unlock-divider-lock.svg)`)

function handleUnlock() {
  emit('unlock')
}

function toggleAutoLock() {
  autoLock.value = !autoLock.value
}

defineExpose({
  autoLock,
})
</script>

<template>
  <div class="chapter-unlock relative pt-40px text-center" :class="{ 'rr-chapter-unlock': isLightTheme }">
    <div class="mask" />

    <div class="unlock-content">
      <div class="rr-unlock-divider" aria-hidden="true">
        <span class="rr-unlock-divider__line" />
        <span class="rr-unlock-divider__icon" />
        <span class="rr-unlock-divider__line" />
      </div>
      <h3 class="theme-h3 rr-unlock-title font-bold text-18px text-center">
        {{ config.texts.unlockTitle }}
      </h3>
      <p class="theme-desc rr-unlock-desc mt-8px lh-24px text-14px text-center" style="white-space: pre-line;">
        {{ config.texts.unlockDesc }}
      </p>

      <a
        class="theme-btn rr-unlock-btn inline-block mt-24px px-24px py-3 min-h-40px lh-1em text-15px text-white text-center btn-solid rounded-full w-300px"
        @click.stop="handleUnlock"
      >
        <span v-if="chapter.priceAmount !== undefined">
          <span v-if="chapter.feeType === CHARGE_TYPE.CHAPTER">{{ t('CoinsToRead2', { 0: chapter.priceAmount }) }}</span>
          <span v-else>{{ t('CoinsToReadBook', { 0: chapter.priceAmount }) }}</span>
        </span>
      </a>

      <div class="theme-radio mt-10px flex justify-center items-center" @click.stop="toggleAutoLock">
        <div class="radio rr-unlock-radio flex justify-center items-center w-14px h-14px inline-block border b-#9296A0 b-rd-14px cursor" :class="{ selected: autoLock }">
          <span class="inline-block w-6px h-6px bg-n-primary b-rd-6px" />
        </div>
        <span class="ml-6px label text-14px rr-unlock-label">{{ config.texts.autoUnlock }}</span>
      </div>

      <p class="theme-balance rr-unlock-balance mt-24px pb-18px lh-24px text-14px text-center">
        {{ t('NovelBalance', { 0: memberCoin }) }}
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.radio {
  span {
    display: none;
  }
  &.selected {
    border-color: var(--primary-color);
    span {
      display: inline-block
    }
  }
}

.chapter-unlock {
  position: relative;

  .mask {
    position: absolute;
    top: -126px;
    left: 0;
    width: 100%;
    height: 126px;
    background: linear-gradient(to bottom, transparent, var(--theme-background, #F6F7F9));
    pointer-events: none;
  }

  .unlock-content {
    position: relative;
    z-index: 1;
  }
}

.rr-chapter-unlock {
  .mask {
    background-image: var(--mask-gradient);
  }
}

.rr-unlock-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 6px 0 40px;
}

.rr-unlock-divider__line {
  width: 122px;
  max-width: 30vw;
  height: 1px;
  background: linear-gradient(to right, var(--unlock-divider-line-color, #D2D6DE) 0%, var(--unlock-divider-line-color, #D2D6DE) 28%, transparent 100%);
}

.rr-unlock-divider__line:first-child {
  background: linear-gradient(to left, var(--unlock-divider-line-color, #D2D6DE) 0%, var(--unlock-divider-line-color, #D2D6DE) 28%, transparent 100%);
}

.rr-unlock-divider__icon {
  width: 22px;
  height: 22px;
  display: inline-block;
  flex-shrink: 0;
  background-color: var(--unlock-divider-color, #C2C9D3);
  -webkit-mask-image: v-bind(unlockDividerLockIcon);
  mask-image: v-bind(unlockDividerLockIcon);
  -webkit-mask-size: 20px 20px;
  mask-size: 20px 20px;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
}

.rr-unlock-title {
  font-size: 24px;
  line-height: 1.2;
  font-weight: 700;
  color: var(--chapter-text-color, currentColor) !important;
  -webkit-text-fill-color: var(--chapter-text-color, currentColor);
}

.rr-unlock-btn {
  border: 0;
  font-size: 16px;
  font-weight: 700;

  &, * {
    color: #fff !important;
    -webkit-text-fill-color: #fff;
  }
}

.rr-unlock-radio {
  span {
    background: var(--theme-radio-bg, var(--primary-color)) !important;
  }
}

.rr-unlock-label {
  color: var(--chapter-text-color, currentColor) !important;
  -webkit-text-fill-color: var(--chapter-text-color, currentColor);
}
</style>
